import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { CircleAlert, Eye, EyeOff, Loader2 } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PUBLIC_FOCUS, PUBLIC_LINK } from "@/components/public/control-classes";
import { useAuth } from "@/contexts/AuthContext";
import { needsAccessResolution } from "@/navigation/member-routing";
import { ACCESS_PENDING_ROUTE } from "@/navigation/routes";
import { takeActivatedEmail } from "@/lib/activation-token";
import { cn } from "@/lib/utils";

interface SignInFormProps {
  className?: string;
  transition: "legacy" | "tenant-internal";
  surface?: "standard" | "gateway";
}

/** Explicit legacy-Murtada / Internal-Mufid transition sign-in controller. */
export function SignInForm({
  className,
  transition,
  surface = "standard",
}: SignInFormProps) {
  const { isAuthenticated, membershipStatus, session, authInitializationError, signIn, signInWithGoogle } = useAuth();
  const [searchParams] = useSearchParams();
  const justActivated = searchParams.get("activated") === "1";
  const [email, setEmail] = useState(takeActivatedEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [callbackRetried, setCallbackRetried] = useState(false);

  if (isAuthenticated) return <Navigate to="/" replace />;
  // Someone already holding a session has nothing to gain from this form, and
  // pressing its Google button only mints another token for the same refusal.
  if (needsAccessResolution(membershipStatus)) {
    return <Navigate to={ACCESS_PENDING_ROUTE} replace />;
  }
  // The redirect above needs `/me` to have answered, and that is a network
  // round trip. Offering the button during it is the same defect one hop
  // earlier: the person presses it and mints another token for a refusal that
  // was already on its way. Gated on a KNOWN session so a logged-out visitor,
  // who has nothing to wait for, still paints the form immediately.
  if (session && membershipStatus === "loading") {
    return (
      <div
        data-auth-form
        data-auth-surface={surface}
        role="status"
        className={cn("flex items-center justify-center gap-2 py-8 text-ui text-muted-foreground", className)}
      >
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Checking your access
      </div>
    );
  }

  const onGoogleSubmit = async () => {
    setError(null);
    setCallbackRetried(true);
    setSubmitting(true);
    // `select_account` is required, not cosmetic. Omitting `prompt` lets Google
    // reuse its one live browser session and return an assertion without ever
    // painting the chooser, so signing out of IMAM and pressing this button
    // lands the previous person straight back in and nobody else can get in at
    // all. This is the entry point where the identity is still being chosen -
    // unlike ActivatePage, which is redeeming one already named by an invitation
    // and deliberately does not force the chooser.
    try {
      const { error: signInError } = await signInWithGoogle(undefined, { selectAccount: true });
      if (signInError) setError("Google sign-in could not be started.");
    } catch {
      setError("Google sign-in could not be started.");
    } finally { setSubmitting(false); }
  };

  const onPasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: signInError } = await signIn(email.trim(), password);
    setSubmitting(false);
    if (signInError) setError("Invalid email or password.");
  };

  if (transition === "legacy") {
    return (
      <form
        data-auth-form
        data-auth-surface={surface}
        onSubmit={onPasswordSubmit}
        className={cn("grid gap-4", className)}
        noValidate
      >
        {justActivated && !error && (
          <p role="status" className="rounded-control bg-well px-3 py-2 text-ui text-foreground">
            Your account is ready. Sign in with the password you just chose.
          </p>
        )}
        {error && <AuthError>{error}</AuthError>}
        <div data-auth-field className="grid gap-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            aria-invalid={error ? true : undefined}
          />
        </div>
        <div data-auth-field className="grid gap-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              aria-invalid={error ? true : undefined}
              className="pr-[var(--control-height)]"
            />
            <button
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              className={`${PUBLIC_FOCUS} absolute inset-y-0 right-0 grid w-[var(--control-height)] place-items-center rounded-r-control text-muted-foreground transition-colors duration-[var(--motion-fast)] ease-[var(--ease-standard)] hover:text-foreground focus-visible:ring-inset`}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
            >
              {showPassword
                ? <EyeOff className="size-4" strokeWidth={1.75} aria-hidden="true" />
                : <Eye className="size-4" strokeWidth={1.75} aria-hidden="true" />}
            </button>
          </div>
        </div>
        <div className="text-right">
          <Link to="/forgot-password" state={{ email: email.trim() }} className={`${PUBLIC_LINK} text-meta text-muted-foreground hover:text-primary hover:underline`}>
            {surface === "gateway" ? "Forgot your password?" : "Forgot password?"}
          </Link>
        </div>
        <Button data-auth-submit type="submit" className="w-full" disabled={submitting}>
          {submitting && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Sign in
        </Button>
      </form>
    );
  }

  return (
    <div
      data-auth-form
      data-auth-surface={surface}
      className={cn("grid gap-4", className)}
    >
      {error ? <AuthError>{error}</AuthError> : authInitializationError && !callbackRetried && (
        <AuthError>Your sign-in could not be completed. Continue with Google to try again.</AuthError>
      )}

      <p className="text-ui text-muted-foreground">
        Use the managed Google account issued by your institution. Google confirms your identity; IMAM separately checks your active invitation and membership.
      </p>

      <Button
        data-auth-submit
        type="button"
        onClick={() => void onGoogleSubmit()}
        className="w-full"
        disabled={submitting}
      >
        {submitting && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
        Continue with Google
      </Button>
    </div>
  );
}

/** The sign-in failure sits where the act is, as the kit's notice. */
function AuthError({ children }: { children: React.ReactNode }) {
  return (
    <Alert variant="destructive">
      <CircleAlert aria-hidden="true" />
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}
